/**
 * Componente presentacional para mostrar información principal del pedido/albarán
 * Lee datos de state.order (cargados por el componente padre)
 */
import React, { useContext } from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Button,
    Divider,
    Stack
} from "@mui/material";
import ReportProblem from "@mui/icons-material/ReportProblem";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import UpdateIcon from "@mui/icons-material/Update";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import FieldLabel from "./FieldLabel";
import InfoField from "./InfoField";
import { StateContext } from "../../../../components/context/StateProvider";
import AuthService from "../../../../services/auth.service";
import Roles from "../../../../config/roles";
import { getOrderStateBadgeStyles } from "../../utils/orderStateColors";
import { BADGE_COLORS } from "../../../../components/ui/badgeColors";
import OrderStates from "../../../../utils/OrderStates";
import { ORDER_TYPE, ORDER_ACTION, ORDER_STATE } from "../../../../config/constants";

const InformacionPedido = ({
    orderNum,
    type, // 'pedido' o 'albaran'
    order, // 'ventas' o 'compras'
    read = false,
    orderStateN2 = false,
    // Flags de flujo: qué campos aplican en la vista actual
    aplicaPreparador = true,
    aplicaRevisor = true,
    aplicaCamara = true,
    // Opciones para selects
    preparadoresOpciones = [],
    revisoresOpciones = [],
    estadosOpciones = [],
    estadosOpcionesN2 = [],
    // Callbacks
    onPreparadorChange,
    onRevisorChange,
    onEstadoChange,
    onEstadoN2Change,
    onCamaraChange,
    onLocalizacionChange,
    onIncidenciaClick,
    // Refs
    camaraRef
}) => {
    const { state } = useContext(StateContext);

    // Color de estado unificado desde helper centralizado
    // Ya no necesitamos esta función, usamos getOrderStateColor directamente

    const getEstadoLabel = (est) => {
        // Primero buscar en opciones de estado N2 si es pedido N2
        if (orderStateN2 && estadosOpcionesN2.length > 0) {
            const estadoEncontrado = estadosOpcionesN2.find(e => e.value === est);
            if (estadoEncontrado) return estadoEncontrado.label;
        }
        // Luego buscar en opciones normales
        if (estadosOpciones.length > 0) {
            const estadoEncontrado = estadosOpciones.find(e => e.value === est);
            if (estadoEncontrado) return estadoEncontrado.label;
        }
        // Fallback: usar mapeo global de OrderStates
        return OrderStates.ESTADOS_MAP[est] || est;
    };

    const formatearNombre = (nombre) => {
        if (!nombre || nombre === '0') return 'Ninguno';
        return nombre.charAt(0).toUpperCase() + nombre.slice(1);
    };

    // Determinar qué mostrar
    const roles = AuthService.getUserRoles();
    const esAdmin = roles.includes(Roles.ADMIN.role);

    const preparador = state.order.preparer;
    const revisor = state.order.reviewer;
    const camara = state.order.camera;
    const localizacion = state.order.location;
    const etiquetadoPor = state.order.sticker?.nameEmployee;
    const pedido = state.order.pedido;
    const estadoN2 = state.order.stateN2;
    const preparadorN2 = state.order.preparerN2;
    const tieneN2 = state.order.n2; // Flag que indica si existen productos en Nave 2
    const ultimaActualizacion = state.order.lastUpdated;
    const tieneIncidencia = state.order.Note !== undefined && state.order.Note.albaran !== undefined && state.order.Note.estado !== 3;
    const completo = state.order.complete;
    const estado = state.order.state;

    const mostrarPreparador = aplicaPreparador && order !== ORDER_ACTION.COMPRAS && preparador !== undefined;
    const mostrarSelectPreparador = mostrarPreparador && !completo && esAdmin;
    const mostrarRevisor = aplicaRevisor && revisor !== undefined && (type === ORDER_TYPE.ALBARAN || (type === ORDER_TYPE.PEDIDO && order === ORDER_ACTION.COMPRAS));
    const mostrarSelectRevisor = mostrarRevisor && !completo && esAdmin;
    const mostrarSelectEstado = !completo && !orderStateN2 && type === ORDER_TYPE.PEDIDO && order === ORDER_ACTION.VENTAS;
    const mostrarSelectEstadoN2 = orderStateN2 && !completo;
    const mostrarCamara = aplicaCamara && camara !== undefined && type === ORDER_TYPE.ALBARAN;
    const mostrarLocalizacion = (!orderStateN2 && type === ORDER_TYPE.PEDIDO && order === ORDER_ACTION.VENTAS) || (!orderStateN2 && type === ORDER_TYPE.ALBARAN && order === ORDER_ACTION.VENTAS && localizacion);
    const mostrarIncidencia = type === ORDER_TYPE.ALBARAN || (type === ORDER_TYPE.PEDIDO && order === ORDER_ACTION.COMPRAS);
    const mostrarUltimaActualizacion = !read && !orderStateN2 && ultimaActualizacion;
    const mostrarInfoN2 = !read && !orderStateN2 && tieneN2; // Verificar el flag n2

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s",
                "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }
            }}
        >
            {/* Header con número y estado */}
            <Box
                sx={{
                    p: 2.5,
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e9ecef",
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                backgroundColor: "#fff",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.06)"
                            }}
                        >
                            <ReceiptLongIcon sx={{ fontSize: 22, color: "#cc071e" }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#6c757d",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    mb: 0.25
                                }}
                            >
                                {type}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: "#212529",
                                    lineHeight: 1.2
                                }}
                            >
                                {state.order.tipo === 'grupo' ? state.order.nombre : orderNum}
                            </Typography>
                        </Box>
                    </Box>
                    {estado && !mostrarSelectEstado && (
                        <Chip
                            label={getEstadoLabel(estado)}
                            sx={{
                                ...getOrderStateBadgeStyles(estado, { height: 28, fontSize: 12 }),
                                letterSpacing: "0.3px"
                            }}
                        />
                    )}
                </Stack>
            </Box>

            {/* Contenido principal */}
            <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack spacing={2.5} sx={{ flex: 1 }}>
                    {/* Select de Estado (si aplica) */}
                    {mostrarSelectEstado && (
                        <Box>
                            <FieldLabel icon={ReceiptLongIcon}>Estado</FieldLabel>
                            <FormControl fullWidth>
                                <Select
                                    value={estado || ''}
                                    onChange={(e) => onEstadoChange && onEstadoChange(e.target.value)}
                                >
                                    {estadosOpciones.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Grid de información - Personal */}
                    {(mostrarPreparador || mostrarRevisor || etiquetadoPor) && (
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#495057",
                                    mb: 1.5,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                Personal
                            </Typography>
                            <Stack spacing={2}>
                                {mostrarPreparador && (
                                    mostrarSelectPreparador ? (
                                        <Box>
                                            <FieldLabel icon={PersonIcon}>Preparador</FieldLabel>
                                            <FormControl fullWidth>
                                                <Select
                                                    value={preparador || '0'}
                                                    onChange={(e) => onPreparadorChange && onPreparadorChange(e.target.value)}
                                                >
                                                    {preparadoresOpciones.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    ) : (
                                        <InfoField icon={PersonIcon} label="Preparador">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {formatearNombre(preparador)}
                                            </Typography>
                                        </InfoField>
                                    )
                                )}

                                {mostrarRevisor && (
                                    mostrarSelectRevisor ? (
                                        <Box>
                                            <FieldLabel icon={VisibilityIcon}>Revisor</FieldLabel>
                                            <FormControl fullWidth>
                                                <Select
                                                    value={revisor || '0'}
                                                    onChange={(e) => onRevisorChange && onRevisorChange(e.target.value)}
                                                >
                                                    {revisoresOpciones.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    ) : (
                                        <InfoField icon={VisibilityIcon} label="Revisor">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {formatearNombre(revisor)}
                                            </Typography>
                                        </InfoField>
                                    )
                                )}

                                {etiquetadoPor && (
                                    <InfoField icon={PersonIcon} label="Etiquetado por">
                                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                            {etiquetadoPor}
                                        </Typography>
                                    </InfoField>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Detalles operativos */}
                    {(pedido || (mostrarCamara && (!completo || camara)) || (mostrarLocalizacion && (!completo || localizacion))) && (
                        <>
                            <Divider />
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#495057",
                                        mb: 1.5,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    Detalles
                                </Typography>
                                <Stack spacing={2}>
                                    {pedido && (
                                        <InfoField icon={ReceiptLongIcon} label="Pedido">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {pedido}
                                            </Typography>
                                        </InfoField>
                                    )}

                                    {mostrarCamara && (
                                        !completo ? (
                                            <Box>
                                                <FieldLabel icon={CameraAltIcon}>Cámara</FieldLabel>
                                                <TextField
                                                    inputRef={camaraRef}
                                                    placeholder="Cámara..."
                                                    fullWidth
                                                    value={camara || ''}
                                                    onChange={(e) => onCamaraChange && onCamaraChange(e.target.value)}
                                                />
                                            </Box>
                                        ) : camara ? (
                                            <InfoField icon={CameraAltIcon} label="Cámara">
                                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                    {camara}
                                                </Typography>
                                            </InfoField>
                                        ) : null
                                    )}

                                    {mostrarLocalizacion && (
                                        !completo ? (
                                            <Box>
                                                <FieldLabel icon={LocationOnIcon}>Localización</FieldLabel>
                                                <TextField
                                                    placeholder="Localización..."
                                                    fullWidth
                                                    value={localizacion || ''}
                                                    onChange={(e) => onLocalizacionChange && onLocalizacionChange(e.target.value)}
                                                />
                                            </Box>
                                        ) : localizacion ? (
                                            <InfoField icon={LocationOnIcon} label="Localización">
                                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                    {localizacion}
                                                </Typography>
                                            </InfoField>
                                        ) : null
                                    )}
                                </Stack>
                            </Box>
                        </>
                    )}

                    {/* Estado N2 */}
                    {orderStateN2 && (
                        <>
                            <Divider />
                            <Box>
                                {mostrarSelectEstadoN2 ? (
                                    <Box>
                                        <FieldLabel icon={ReceiptLongIcon}>Estado</FieldLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                value={estado || ''}
                                                onChange={(e) => onEstadoN2Change && onEstadoN2Change(e.target.value)}
                                            >
                                                {estadosOpcionesN2.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                ) : estado && (
                                    <InfoField icon={ReceiptLongIcon} label="Estado">
                                        <Chip
                                            label={getEstadoLabel(estado)}
                                            size="small"
                                            sx={getOrderStateBadgeStyles(estado, { height: 22, fontSize: 12 })}
                                        />
                                    </InfoField>
                                )}
                            </Box>
                        </>
                    )}

                    {/* Info Nave 2 - Solo se muestra si existen productos en Nave 2 */}
                    {mostrarInfoN2 && (
                        <>
                            <Divider />
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#495057",
                                        mb: 1.5,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    Nave 2
                                </Typography>
                                <Stack spacing={2}>
                                    {estadoN2 && (
                                        <InfoField icon={WarehouseIcon} label="Estado Nave 2">
                                            <Chip
                                                label={getEstadoLabel(estadoN2)}
                                                size="small"
                                                sx={getOrderStateBadgeStyles(estadoN2, { height: 22, fontSize: 12 })}
                                            />
                                        </InfoField>
                                    )}
                                    {preparadorN2 && preparadorN2 !== '0' && (
                                        <InfoField icon={PersonIcon} label="Preparador Nave 2">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {formatearNombre(preparadorN2)}
                                            </Typography>
                                        </InfoField>
                                    )}
                                </Stack>
                            </Box>
                        </>
                    )}

                    {/* Footer con última actualización e incidencia */}
                    {(mostrarUltimaActualizacion || mostrarIncidencia) && (
                        <>
                            <Divider />
                            <Stack spacing={1.5}>
                                {mostrarUltimaActualizacion && (
                                    <InfoField icon={UpdateIcon} label="Última actualización">
                                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#495057" }}>
                                            {ultimaActualizacion}
                                        </Typography>
                                    </InfoField>
                                )}

                                {mostrarIncidencia && (
                                    <InfoField icon={ReportProblem} label="Incidencia">
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={onIncidenciaClick}
                                                sx={{
                                                    textTransform: "none",
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: "#cc071e",
                                                    p: 0,
                                                    minWidth: "auto",
                                                    "&:hover": {
                                                        backgroundColor: "transparent",
                                                        color: "#a30618",
                                                        textDecoration: "underline"
                                                    }
                                                }}
                                            >
                                                Ver incidencia
                                            </Button>
                                            {tieneIncidencia && (
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        backgroundColor: BADGE_COLORS.AMBER.border,
                                                        animation: "pulse 2s ease-in-out infinite",
                                                        "@keyframes pulse": {
                                                            "0%, 100%": { opacity: 1 },
                                                            "50%": { opacity: 0.5 }
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </InfoField>
                                )}
                            </Stack>
                        </>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

export default InformacionPedido;
